/**
 * RockBoardCard
 * Compact draggable card for the Planning Board Kanban columns.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { RockStatusBadge } from './RockStatusBadge';
import type { Rock } from '../types';

interface RockBoardCardProps {
  rock: Rock;
  onEdit: (rock: Rock) => void;
}

function formatDueDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function isPastDue(dateStr: string, status: Rock['status']): boolean {
  return new Date(dateStr) < new Date() && status !== 'complete' && status !== 'canceled';
}

export function RockBoardCard({ rock, onEdit }: RockBoardCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rock.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const deliverables = rock.deliverables ?? [];
  const deliverableTotal = deliverables.length;
  const deliverableDone = deliverables.filter((d) => d.completed).length;

  const overdue = isPastDue(rock.dueDate, rock.status);

  // Derive owner initials from ownerId — in the absence of a name we show the
  // first 2 chars of the UUID which at least distinguishes owners visually.
  const ownerInitials = rock.ownerId ? rock.ownerId.substring(0, 2).toUpperCase() : '?';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'cursor-grab active:cursor-grabbing select-none transition-shadow',
        isDragging && 'opacity-40 shadow-lg ring-2 ring-primary',
        'hover:shadow-md'
      )}
      onClick={(e) => {
        if (!isDragging) {
          e.stopPropagation();
          onEdit(rock);
        }
      }}
    >
      <CardContent className="p-3 space-y-2">
        {/* Status badge — compact */}
        <RockStatusBadge status={rock.status} className="text-[10px] px-1.5 py-0.5" />

        {/* Title — truncate to 2 lines */}
        <p className="text-sm font-medium leading-snug line-clamp-2">{rock.title}</p>

        {/* Footer row */}
        <div className="flex items-center justify-between pt-0.5">
          <div className="flex items-center gap-2">
            {/* Due date */}
            <div
              className={cn(
                'flex items-center gap-1 text-xs',
                overdue ? 'text-destructive font-medium' : 'text-muted-foreground'
              )}
            >
              <Calendar className="h-3 w-3 shrink-0" />
              <span>{formatDueDate(rock.dueDate)}</span>
            </div>

            {/* Deliverable count */}
            {deliverableTotal > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Target className="h-3 w-3 shrink-0" />
                <span>
                  {deliverableDone}/{deliverableTotal}
                </span>
              </div>
            )}
          </div>

          {/* Owner avatar */}
          <Avatar className="h-5 w-5 shrink-0">
            <AvatarFallback className="text-[9px] bg-muted">{ownerInitials}</AvatarFallback>
          </Avatar>
        </div>
      </CardContent>
    </Card>
  );
}
